import { CONFIG } from "../config.js";
import type { ScoringEvent } from "./score.js";

/**
 * Update the multiplier based on a scoring event. All values are in X10 format.
 *
 * @param currentX10 - Current multiplier in X10 format (e.g. 15 = 1.5x)
 * @param event - The scoring event
 * @param gamesPlayed - Number of games the player has completed
 * @returns Updated multiplier in X10 format
 */
export function updateMultiplier(
  currentX10: number,
  event: ScoringEvent,
  gamesPlayed: number,
): number {
  let next = currentX10;

  if (event.clustersPopped > 0) {
    // Hit: +0.5x
    next += 5;

    // Near failure: additional +1.0x
    if (event.nearFailure) {
      next += 10;
    }

    // Cap at maximum
    return Math.min(next, CONFIG.MAX_MULTIPLIER_X10);
  }

  // Setup shot: no penalty, no gain.
  if (event.isSetupShot) {
    return next;
  }

  // Governor Lock: no decay on miss
  if (event.governorLocked) {
    return next;
  }

  // Miss: decay based on experience
  const decay =
    gamesPlayed < 3
      ? CONFIG.MULTIPLIER_DECAY_ONBOARDING
      : CONFIG.MULTIPLIER_DECAY_STANDARD;

  next -= decay;

  // Floor at minimum
  return Math.max(next, CONFIG.MIN_MULTIPLIER_X10);
}
